import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, Upload, CornerDownLeft } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { hasDistrict, parsePostcodeList, searchDistricts, silverTotalFor } from "@/lib/audienceEngine";
import { areaNameFor } from "@/data/postcodeAreas";
import { cn } from "@/lib/utils";

export function PostcodeChip({ code, onRemove, size = "lg" }) {
  return (
    <motion.span layout initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7, transition: { duration: 0.22 } }}
      transition={{ type: "spring", stiffness: 420, damping: 24 }}
      className={cn("chip-lime inline-flex items-center gap-2 rounded-full font-ui font-semibold tracking-[0.06em]", size === "lg" ? "py-1.5 pl-3.5 pr-1.5 text-[15px]" : "py-1 pl-3 pr-1 text-[13px]")}
      data-testid={`postcode-chip-${code}`}>
      {code}
      <button type="button" onClick={() => onRemove(code)} aria-label={`Remove ${code}`} className="grid h-6 w-6 place-items-center rounded-full text-white/70 transition-colors hover:bg-white/15 hover:text-white" data-testid={`remove-postcode-${code}`}>
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.span>
  );
}

function Suggestion({ code, query, active, id, onPick, onHover }) {
  const q = query.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return (
    <li id={id} role="option" aria-selected={active} onMouseDown={(e) => { e.preventDefault(); onPick(code); }} onMouseEnter={onHover}
      className={cn("flex cursor-pointer items-center justify-between gap-4 rounded-xl px-4 py-3 transition-colors", active ? "bg-[#96C11F]/15" : "hover:bg-white/5")}
      data-testid={`postcode-suggestion-${code}`}>
      <span className="flex items-baseline gap-3">
        <span className="font-ui text-[15px] font-semibold tracking-[0.04em] text-white"><span className="text-[#C6EE5A]">{code.slice(0, q.length)}</span>{code.slice(q.length)}</span>
        <span className="text-[13px] text-white/45">{areaNameFor(code)}</span>
      </span>
      <span className="font-ui text-[12px] text-white/50 num">{silverTotalFor(code).toLocaleString("en-GB")} homeowner households</span>
    </li>
  );
}

export function PostcodeSearch({ variant = "hero", onAdded, autoFocus = false, showChips = true, direction = "down" }) {
  const { areas, addAreas, removeArea, clearAreas } = useSilver();
  const [value, setValue] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [navigated, setNavigated] = useState(false);
  const [notice, setNotice] = useState(null);
  const inputRef = useRef(null);
  const listId = useId();
  const hero = variant === "hero";
  const card = variant === "card";
  const up = direction === "up";
  const suggestions = useMemo(() => searchDistricts(value, { exclude: areas }), [value, areas]);
  const showList = open && suggestions.length > 0;

  useEffect(() => { setActive(0); setNavigated(false); }, [value]);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), 4600);
    return () => clearTimeout(id);
  }, [notice]);

  const commit = (codes) => {
    const res = addAreas(codes);
    if (res.invalid.length) setNotice({ tone: "error", text: `We don't currently have audience data for ${res.invalid.join(", ")}.` });
    else if (res.duplicate.length && !res.added.length) setNotice({ tone: "info", text: `${res.duplicate.join(", ")} ${res.duplicate.length > 1 ? "are" : "is"} already in your patch.` });
    else if (res.added.length > 1) setNotice({ tone: "ok", text: `Added ${res.added.length} districts to your patch.` });
    if (res.added.length) onAdded?.(res.added);
  };

  const commitTyped = () => {
    const tokens = parsePostcodeList(value);
    if (!tokens.length) return;
    if (tokens.length === 1) {
      const t = tokens[0];
      if (navigated && suggestions[active]) commit([suggestions[active]]);
      else if (hasDistrict(t) || !suggestions.length) commit([t]);
      else {
        setNotice({ tone: "info", text: `Choose a district starting ${t} — for example ${suggestions.slice(0, 3).join(", ")}.` });
        return;
      }
    } else commit(tokens);
    setValue("");
  };

  const onChange = (e) => {
    const v = e.target.value.toUpperCase();
    if (/[,;]/.test(v)) {
      const parts = v.split(/[,;]/);
      const complete = parsePostcodeList(parts.slice(0, -1).join(","));
      if (complete.length) commit(complete);
      setValue(parts[parts.length - 1].trimStart());
    } else setValue(v);
    setOpen(true);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" && suggestions.length) {
      e.preventDefault();
      setOpen(true);
      setNavigated(true);
      setActive((a) => (a + 1) % suggestions.length);
    } else if (e.key === "ArrowUp" && suggestions.length) {
      e.preventDefault();
      setNavigated(true);
      setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      commitTyped();
    } else if (e.key === "Escape") setOpen(false);
    else if (e.key === "Backspace" && showChips && !value && areas.length) removeArea(areas[areas.length - 1]);
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData("text");
    if (/[\s,;\n]/.test(text.trim())) {
      e.preventDefault();
      commit(parsePostcodeList(text));
      setValue("");
    }
  };

  return (
    <div className="relative" data-testid={`postcode-search-${variant}`}>
      <div className={cn("field flex flex-wrap items-center gap-2", hero ? "min-h-[76px] rounded-[20px] px-4 py-3 sm:px-5" : "min-h-[56px] rounded-2xl px-3 py-2")} onClick={() => inputRef.current?.focus()}>
        <Search className={cn("shrink-0 text-white/40", hero ? "h-5 w-5" : "h-4 w-4")} aria-hidden="true" />
        <AnimatePresence initial={false}>
          {showChips && areas.map((code) => (
            <PostcodeChip key={code} code={code} onRemove={removeArea} size={hero ? "lg" : "sm"} />
          ))}
        </AnimatePresence>
        <input ref={inputRef} value={value} onChange={onChange} onKeyDown={onKeyDown} onPaste={onPaste} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
          placeholder={areas.length ? "Add another district…" : card ? "e.g. RG40, RG41" : "Try RG40, RG41 or SW18"}
          className={cn("min-w-[150px] flex-1 bg-transparent font-ui font-medium uppercase tracking-[0.04em] text-white outline-none placeholder:normal-case placeholder:tracking-normal placeholder:text-white/35", hero ? "py-2 text-xl sm:text-2xl" : "py-1.5 text-[15px]")}
          role="combobox" aria-expanded={showList} aria-controls={listId} aria-autocomplete="list" aria-label="Add postcode districts"
          aria-activedescendant={showList ? `${listId}-opt-${active}` : undefined} autoComplete="off" spellCheck="false" data-testid="postcode-input" />
        {value && (
          <button type="button" onClick={commitTyped} className="hidden items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 font-ui text-[11px] font-semibold uppercase tracking-[0.1em] text-white/80 hover:bg-white/15 sm:inline-flex" data-testid="add-postcode-btn">
            Add <CornerDownLeft className="h-3 w-3" />
          </button>
        )}
      </div>

      <AnimatePresence>
        {showList && (
          <motion.ul id={listId} role="listbox" initial={{ opacity: 0, y: up ? 6 : -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: up ? 6 : -6 }} transition={{ duration: 0.2 }}
            className={cn("glass-strong absolute left-0 right-0 z-30 max-h-80 overflow-auto !rounded-2xl p-2 thin-scroll", up ? "bottom-[calc(100%+8px)]" : "top-[calc(100%+8px)]")} data-testid="postcode-suggestions">
            {suggestions.map((code, i) => (
              <Suggestion key={code} id={`${listId}-opt-${i}`} code={code} query={value} active={i === active} onPick={(c) => { commit([c]); setValue(""); }} onHover={() => setActive(i)} />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      <div className="mt-3 flex min-h-[28px] flex-wrap items-center justify-between gap-3">
        <div aria-live="polite" className="min-h-[20px]">
          <AnimatePresence mode="wait">
            {notice && (
              <motion.p key={notice.text} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
                className={cn("text-[13px]", notice.tone === "error" ? "text-[#FDE68A]" : notice.tone === "ok" ? "text-[#C6EE5A]" : "text-white/60")} data-testid="postcode-notice">
                {notice.text}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        {(hero || card) && (
          <div className="flex items-center gap-2">
            {hero && <span className="hidden font-ui text-[11px] text-white/35 md:inline">Enter or comma to add · paste a list</span>}
            {hero && (
              <button type="button" disabled title="Coming soon" className="pill !py-1.5 opacity-50" data-testid="upload-territory-btn">
                <Upload className="h-3.5 w-3.5" /> Upload territory
              </button>
            )}
            {areas.length > 0 && (
              <button type="button" onClick={clearAreas} className="pill !py-1.5" data-testid="clear-all-postcodes-btn">Clear all</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
